import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart,
  Mail,
  Lock,
  User,
  MapPin,
  Calendar,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  Lock as LockIcon,
  Clock,
} from "lucide-react";
import { signUp } from "../lib/authClient";
import { api, ApiError } from "../lib/api";
import { DateWheelPicker } from "../components/DateWheelPicker";

type Gender = "male" | "female" | "diverse";

const GENDERS: { value: Gender; label: string }[] = [
  { value: "male", label: "Männlich" },
  { value: "female", label: "Weiblich" },
  { value: "diverse", label: "Divers" },
];

const TRUST = [
  { icon: ShieldCheck, label: "DSGVO" },
  { icon: LockIcon, label: "SSL verschlüsselt" },
  { icon: Clock, label: "30 Tage gratis" },
];

export function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [gender, setGender] = useState<Gender | "">("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [birthday, setBirthday] = useState("");
  const [plz, setPlz] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!gender) {
      setError("Bitte wähle dein Geschlecht.");
      return;
    }
    if (password.length < 12) {
      setError("Passwort muss mindestens 12 Zeichen haben.");
      return;
    }
    setSubmitting(true);
    try {
      const signUpResult = await signUp.email({
        email,
        password,
        name: displayName,
      });
      if (signUpResult.error) {
        const code = (signUpResult.error as { code?: string }).code ?? "";
        const msg =
          signUpResult.error.message ?? "Registrierung fehlgeschlagen.";
        const status = (signUpResult.error as { status?: number }).status;
        if (code === "USER_ALREADY_EXISTS" || /exist/i.test(msg)) {
          setError("Diese E-Mail ist bereits registriert — melde dich an.");
        } else if (status === 429) {
          setError(
            "Zu viele Versuche. Warte eine Minute und probiere es erneut.",
          );
        } else if (/password/i.test(msg)) {
          setError("Passwort zu kurz oder ungültig (min. 12 Zeichen).");
        } else {
          setError(`Registrierung fehlgeschlagen: ${msg}`);
        }
        setSubmitting(false);
        return;
      }
      await api.post("/api/profile", {
        displayName,
        gender,
        birthdate: birthday,
        plz,
      });
      navigate("/onboarding/fotos", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "underage") {
          setError("Du musst mindestens 18 Jahre alt sein.");
        } else if (err.code === "unknown_plz") {
          setError("Diese PLZ ist uns noch nicht bekannt.");
        } else {
          setError("Profil konnte nicht angelegt werden: " + err.message);
        }
      } else {
        setError("Unerwarteter Fehler. Bitte versuche es gleich noch einmal.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen bg-white text-ink-900">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[460px] overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-200/50 blur-3xl" />
        <div className="absolute top-10 right-10 h-64 w-64 rounded-full bg-sunset-400/30 blur-3xl" />
        <div className="absolute top-20 left-10 h-64 w-64 rounded-full bg-brand-100 blur-3xl" />
      </div>

      <header className="container flex items-center justify-between py-5">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-gradient shadow-glow">
            <Heart className="h-5 w-5 text-white" fill="currentColor" />
          </span>
          <span className="text-xl font-bold tracking-tight">Parble</span>
        </Link>
        <Link
          to="/login"
          className="text-sm font-semibold text-ink-500 hover:text-ink-900 transition"
        >
          Anmelden
        </Link>
      </header>

      <main className="container flex min-h-[calc(100vh-88px)] items-start justify-center py-8 md:py-12">
        <div className="w-full max-w-lg animate-fade-in-up">
          <div className="card p-8 md:p-10">
            <div className="text-center">
              <div className="chip mb-4">
                <Clock className="h-3.5 w-3.5" />
                30 Tage kostenlos
              </div>
              <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
                Finde, wer wirklich{" "}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  passt.
                </span>
              </h1>
              <p className="mt-2 text-[15px] text-ink-500">
                Danach 5 € pro Monat. Für alle gleich.
              </p>
            </div>

            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div
                  role="alert"
                  className="rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-700"
                >
                  {error}
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-sm font-semibold text-ink-700"
                >
                  E-Mail
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-300" />
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="du@beispiel.de"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-11"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-sm font-semibold text-ink-700"
                >
                  Passwort
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-300" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={12}
                    autoComplete="new-password"
                    placeholder="Mindestens 12 Zeichen"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-11 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword ? "Passwort verbergen" : "Passwort anzeigen"
                    }
                    className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-ink-500 hover:bg-surface hover:text-ink-900 transition"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-ink-500">
                  Mindestens 12 Zeichen &mdash; Buchstaben, Zahlen, Symbole.
                </p>
              </div>

              <div>
                <label
                  htmlFor="firstName"
                  className="mb-1.5 block text-sm font-semibold text-ink-700"
                >
                  Vorname
                </label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-300" />
                  <input
                    id="firstName"
                    type="text"
                    required
                    minLength={2}
                    maxLength={40}
                    autoComplete="given-name"
                    placeholder="So wirst du bei Parble heißen"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input-field pl-11"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-ink-700">
                  <Calendar className="h-4 w-4 text-ink-300" />
                  Geburtstag
                </label>
                <DateWheelPicker value={birthday} onChange={setBirthday} />
                <p className="mt-1.5 text-xs text-ink-500">
                  Mindestalter 18 — tippen und scrollen, um dein Datum zu
                  wählen.
                </p>
              </div>

              <div>
                <label
                  htmlFor="zip"
                  className="mb-1.5 block text-sm font-semibold text-ink-700"
                >
                  PLZ
                </label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-300" />
                  <input
                    id="zip"
                    type="text"
                    inputMode="numeric"
                    required
                    pattern="\d{5}"
                    maxLength={5}
                    autoComplete="postal-code"
                    placeholder="10115"
                    value={plz}
                    onChange={(e) => setPlz(e.target.value)}
                    className="input-field pl-11"
                  />
                </div>
              </div>

              <fieldset>
                <legend className="mb-1.5 block text-sm font-semibold text-ink-700">
                  Geschlecht
                </legend>
                <div className="grid grid-cols-3 gap-2">
                  {GENDERS.map(({ value, label }) => {
                    const active = gender === value;
                    return (
                      <label
                        key={value}
                        className={`relative flex cursor-pointer items-center justify-center rounded-full border px-4 py-3 text-sm font-semibold transition-all duration-200 active:scale-[0.98] ${
                          active
                            ? "border-transparent bg-brand-gradient text-white shadow-glow"
                            : "border-border bg-white text-ink-700 hover:border-ink-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="gender"
                          value={value}
                          checked={active}
                          onChange={() => setGender(value)}
                          className="sr-only"
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <label className="flex items-start gap-3 rounded-2xl border border-border bg-surface px-4 py-3 text-sm text-ink-700">
                <input
                  type="checkbox"
                  required
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-ink-300 accent-brand-500"
                />
                <span className="leading-snug">
                  Ich bin volljährig (18+) und akzeptiere die{" "}
                  <Link
                    to="/legal/agb"
                    className="font-semibold text-brand-500 hover:text-brand-600 transition"
                  >
                    AGB
                  </Link>{" "}
                  sowie die{" "}
                  <Link
                    to="/legal/datenschutz"
                    className="font-semibold text-brand-500 hover:text-brand-600 transition"
                  >
                    Datenschutzerklärung
                  </Link>
                  .
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting || !accepted || !gender || !email || !password || !displayName || !birthday || !plz}
                className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-glow"
              >
                {submitting ? "Konto wird angelegt …" : "Kostenlos registrieren"}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>

            <div className="mt-7 flex items-center justify-center gap-6 border-t border-border pt-6">
              {TRUST.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 text-xs font-semibold text-ink-500"
                >
                  <Icon className="h-4 w-4 text-brand-500" />
                  {label}
                </div>
              ))}
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-ink-500">
            Bereits registriert?{" "}
            <Link
              to="/login"
              className="font-semibold text-brand-500 hover:text-brand-600 transition"
            >
              Anmelden
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
